// Detalle de una liquidación (PaymentSettlement). Accedida desde el
// historial en /liquidaciones o recién creada desde ahí mismo. Permite
// revertirla — operación de plata real, exige confirmación explícita — y
// listar los pagos que agrupa, cada uno con link a su origen.

import { useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useSettlement, useRevertSettlement } from "./hooks";
import { usePaymentMethods } from "../payment-methods/hooks";
import { fullName, useCustomers } from "../customers/hooks";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { Button } from "../../components/Button";
import { Sheet } from "../../components/Sheet";
import { PageHeader } from "../../components/PageHeader";
import { Toast } from "../../components/Toast";
import { formatARS } from "../../lib/money";
import { ApiError } from "../../lib/api";
import { ArrowLeftRightIcon, BanknoteIcon, CheckIcon, ChevronRightIcon, CreditCardIcon, UserIcon } from "../../components/icons";

// Mismo mapeo icon -> glifo que AdminPaymentMethodsView/SaleView (no
// exportado desde ahí, se repite acá por consistencia visual). Todas las
// filas de esta liquidación comparten el mismo medio de pago.
function MethodGlyph({ icon }: { icon?: string }) {
  if (icon === "ArrowLeftRight") return <ArrowLeftRightIcon />;
  if (icon === "CreditCard") return <CreditCardIcon />;
  if (icon === "UserCircle") return <UserIcon />;
  return <BanknoteIcon />;
}

export function SettlementDetailView() {
  const { id } = useParams<{ id: string }>();
  const settlementId = id ? Number(id) : null;
  const settlementQ = useSettlement(settlementId);
  const revertSettlement = useRevertSettlement();
  const location = useLocation();
  const methodsQ = usePaymentMethods();
  const customersQ = useCustomers({ includeInactive: true, limit: 200 });

  const justCreated = (location.state as { justCreated?: boolean } | null)?.justCreated ?? false;
  const [showCreatedToast, setShowCreatedToast] = useState(justCreated);

  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const settlement = settlementQ.data;
  const method = methodsQ.data?.find((candidate) => candidate.id === settlement?.paymentMethodId);
  const customerNameById = new Map((customersQ.data ?? []).map((customer) => [customer.id, fullName(customer)]));

  if (settlementQ.isLoading) {
    return (
      <main style={centerStyle}>
        <PageHeader title="Liquidación" backHref="/liquidaciones" />
        <p style={{ padding: "var(--space-4)", color: "var(--text-secondary)" }}>Cargando...</p>
      </main>
    );
  }

  if (!settlement) {
    return (
      <main style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <PageHeader title="Liquidación" backHref="/liquidaciones" />
        <p style={{ padding: "var(--space-4)" }}>No se encontró la liquidación.</p>
      </main>
    );
  }

  const commissionPct = settlement.grossCents > 0 ? (settlement.commissionCents / settlement.grossCents) * 100 : null;

  const doRevert = async () => {
    setRevertError(null);
    try {
      await revertSettlement.mutateAsync(settlement.id);
      setRevertConfirmOpen(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setRevertError("Esta liquidación ya había sido revertida.");
      } else {
        setRevertError("No se pudo revertir. Volvé a intentar.");
      }
    }
  };

  return (
    <main className="sale-detail-page">
      <PageHeader title="Liquidación" backHref="/liquidaciones" />

      {showCreatedToast && (
        <Toast
          kind="success"
          message="Liquidación confirmada."
          icon={<CheckIcon />}
          onDismiss={() => setShowCreatedToast(false)}
          autoCloseMs={4000}
        />
      )}

      <div className="sale-detail-content">
        <Card>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p className="eyebrow">{settlement.methodName}</p>
              <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-12)", marginTop: 4 }}>Transferido</span>
              <strong className="num" style={{ fontSize: "clamp(28px,8vw,38px)", lineHeight: 1, letterSpacing: "-0.03em", marginTop: 2 }}>
                {formatARS(settlement.transferredCents)}
              </strong>
            </div>
            {settlement.status === "reverted" ? (
              <Chip variant="tag-danger" size="sm">Revertida</Chip>
            ) : (
              <Chip variant="tag-success" size="sm">Activa</Chip>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-4)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--border-subtle)" }}>
            <Row label="Fecha de liquidación" value={new Date(`${settlement.settledOn}T00:00:00`).toLocaleDateString("es-AR")} />
            <Row label="Medio" value={method?.name ?? settlement.methodName} />
            <Row label="Bruto liquidado" value={formatARS(settlement.grossCents)} />
            <Row
              label="Comisión"
              value={commissionPct !== null ? `${formatARS(settlement.commissionCents)} (${commissionPct.toFixed(1)}%)` : formatARS(settlement.commissionCents)}
            />
            {settlement.note && <Row label="Nota" value={settlement.note} />}
          </div>
        </Card>

        <Card className="sale-detail-card">
          <div className="sale-detail-section-heading">
            <div><p className="eyebrow">Incluye</p><strong>Pagos liquidados</strong></div>
            <span>{settlement.payments.length} {settlement.payments.length === 1 ? "pago" : "pagos"}</span>
          </div>
          <div className="entity-list">
            {settlement.payments.map((payment) => {
              const isAccountCollection = payment.sourceType === "account_settlement";
              const target = isAccountCollection ? `/customers/${payment.customerId}` : `/sales/${payment.saleId}`;
              const title = isAccountCollection
                ? customerNameById.get(payment.customerId) ?? `Cliente #${payment.customerId}`
                : `Venta #${payment.saleId}`;
              const occurredAt = isAccountCollection ? payment.occurredAt : payment.saleCreatedAt;
              return (
              <Link
                key={isAccountCollection ? `account_settlement:${payment.accountSettlementId}` : `sale_payment:${payment.salePaymentId}`}
                to={target}
                className="entity-row"
              >
                <span className={`entity-row__icon payment-method-icon payment-${method?.color ?? "other"}`}>
                  <MethodGlyph icon={method?.icon} />
                </span>
                <div className="entity-row__body">
                  <span className={`settlement-source-tag settlement-source-tag--${isAccountCollection ? "account" : "sale"}`}>
                    {isAccountCollection ? "Cobro de cuenta" : "Venta"}
                  </span>
                  <strong>{title}</strong>
                  <small>{new Date(occurredAt).toLocaleDateString("es-AR")}</small>
                </div>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  <strong className="num">{formatARS(payment.amountCents)}</strong>
                  {payment.sourceType === "sale_payment" && payment.saleStatus === "voided" && (
                    <span className="status-badge status-badge--danger">Anulada</span>
                  )}
                </span>
                <ChevronRightIcon style={{ justifySelf: "center", color: "var(--text-secondary)" }} />
              </Link>
              );
            })}
          </div>
        </Card>

        {settlement.status === "reverted" && (
          <Card style={{ background: "var(--surface-sunken)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <Chip variant="tag-danger" size="sm">Revertida</Chip>
              <strong>Liquidación revertida</strong>
            </div>
            {settlement.revertedAt && (
              <p style={{ margin: "var(--space-1) 0 0", color: "var(--text-secondary)" }}>
                {new Date(settlement.revertedAt).toLocaleString("es-AR")}. Los pagos volvieron a quedar pendientes de liquidación.
              </p>
            )}
          </Card>
        )}

        {settlement.status === "active" && (
          <div style={{ marginTop: "var(--space-2)" }}>
            <Button
              variant="danger"
              fullWidth
              size="lg"
              onClick={() => { setRevertError(null); setRevertConfirmOpen(true); }}
              disabled={revertSettlement.isPending}
            >
              Revertir liquidación
            </Button>
          </div>
        )}
      </div>

      <Sheet open={revertConfirmOpen} onClose={() => setRevertConfirmOpen(false)} title="Revertir liquidación">
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <p style={{ margin: 0, color: "var(--text-secondary)" }}>
            Esta acción no se puede deshacer desde acá. Los {settlement.payments.length} pagos incluidos volverán a quedar pendientes de liquidación y esta liquidación dejará de contar en el resumen del día.
          </p>
          {revertError && <p className="form-error" style={{ margin: 0 }}>{revertError}</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <Button variant="danger" fullWidth size="lg" disabled={revertSettlement.isPending} onClick={doRevert}>
              {revertSettlement.isPending ? "Revirtiendo..." : "Revertir liquidación"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setRevertConfirmOpen(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Sheet>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--font-size-14)" }}>
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
